/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/**
 * @author Granite Bagas
 */
import React from 'react';
import { TouchableOpacity, Animated, StyleSheet } from 'react-native';
import { useField } from 'formik';

import Colors from '@global/colors';

const SIZE_PRESETS = {
  small: { width: 48, height: 28, circleSize: 20, padding: 4 },
  medium: { width: 72, height: 40, circleSize: 28, padding: 6 },
  large: { width: 96, height: 56, circleSize: 40, padding: 8 },
};

const ToggleSwitch = ({
  name = '',
  value = false,
  onValueChange = () => {},
  size = 'large',
  width,
  height,
  circleSize,
  padding,
  disabled = false,
  style = {},
}) => {
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.large;
  const _width = width ?? preset.width;
  const _height = height ?? preset.height;
  const _circleSize = circleSize ?? preset.circleSize;
  const _padding = padding ?? preset.padding;

  let isOn;
  let setValue;
  if (name) {
    const [field, , helpers] = useField(name);
    isOn = !!field.value;
    setValue = helpers.setValue;
  } else if (typeof value === 'boolean') {
    isOn = value;
    setValue = onValueChange;
  } else {
    const [internal, setInternal] = React.useState(false);
    isOn = internal;
    setValue = (v) => {
      setInternal(v);
      if (onValueChange) {
        onValueChange(v);
      }
    };
  }

  const anim = React.useRef(new Animated.Value(isOn ? 1 : 0)).current;
  React.useEffect(() => {
    Animated.timing(anim, {
      toValue: isOn ? 1 : 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [isOn]);

  const backgroundColor = anim.interpolate({
    inputRange: [0, 1],
    outputRange: ['#eaeaea', '#0037ff'],
  });

  const translateX = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, _width - _circleSize - _padding * 2],
  });

  const handlePress = () => {
    if (disabled) {
      return;
    }
    setValue && setValue(!isOn);
  };

  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={handlePress}
      disabled={disabled}
      style={[
        { alignItems: 'center', justifyContent: 'center' },
        style,
        disabled && { opacity: 0.5 },
      ]}>
      <Animated.View
        style={[
          styles.switch,
          {
            width: _width,
            height: _height,
            borderRadius: _height / 2,
            backgroundColor,
            padding: _padding,
          },
        ]}>
        <Animated.View
          style={[
            styles.circle,
            {
              width: _circleSize,
              height: _circleSize,
              borderRadius: _circleSize / 2,
              transform: [{ translateX }],
              top: _padding,
              left: _padding,
            },
          ]}
        />
      </Animated.View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  switch: {
    justifyContent: 'center',
  },
  circle: {
    backgroundColor: Colors.white,
    position: 'absolute',
    shadowColor: Colors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
});

export default ToggleSwitch;
